import type { RouterContextType } from "@/api/permissions/permissions.api";
import { SUPER_ADMIN } from "@/components/data/constants";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { redirect } from "@tanstack/react-router";

type BeforeLoadArgs = {
  context: RouterContextType;
  location: { pathname: string };
};

/**
 * Gates the short-link admin screens on super admin, EXPLICITLY.
 *
 * This cannot rely on authGuardWithPermissions alone. That guard resolves a
 * module from the path and, when it finds none, allows the request through:
 * "If path is not tied to a protected module, allow access" (authGuard.ts).
 * Since /short-links is deliberately not in requiredModuleByPath — there is no
 * deeplink permission code, and introducing one needs a per-tenant Permissions
 * backfill — the default would let every authenticated user reach the screen,
 * including service providers. The API would refuse them, but the screen would
 * render first and then fail, which is a worse experience and a fail-open
 * route guard besides.
 *
 * Matches the backend exactly: the deeplink routes authorize against a derived
 * "deeplink" code that no Permissions row carries, so only a super admin gets
 * through. When that changes, this and the backend must change together.
 */
export async function requireSuperAdmin({ context, location }: BeforeLoadArgs) {
  await authGuardWithPermissions({ context, location });

  const { userType } = getActiveSessionState();
  if (userType !== SUPER_ADMIN) {
    throw redirect({ to: "/" });
  }
}
